import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Shirt, Mail, Lock, User, Loader2, AlertCircle } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo ha ido mal. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AuthPage({ mode = "login" }) {
  const { loginWithPassword, register, login } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(mode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (tab === "login") await loginWithPassword(email, password);
      else await register(email, password, name);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background text-foreground">
      {/* Brand side */}
      <div className="hidden lg:flex flex-col justify-between bg-[#002FA7] text-white p-12 relative overflow-hidden">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-white flex items-center justify-center rounded-sm"><Shirt className="w-5 h-5 text-[#002FA7]" strokeWidth={2.2} /></div>
          <span className="font-display text-xl tracking-tight font-semibold">MOCKUP STUDIO</span>
        </div>
        <div className="relative z-10">
          <h1 className="font-display text-5xl tracking-tight font-semibold leading-[1.05] mb-4">Diseña, previsualiza<br />y produce tu ropa.</h1>
          <p className="text-white/70 max-w-sm">Crea mockups realistas con IA, gestiona la producción y controla tus créditos. Empieza con 5 créditos gratis.</p>
        </div>
        <div className="text-white/50 text-xs uppercase tracking-[0.2em] relative z-10">Herramienta interna de producción</div>
        <div className="absolute -right-20 -bottom-20 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute right-24 top-24 w-40 h-40 rounded-full bg-white/5" />
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="w-8 h-8 bg-[#002FA7] flex items-center justify-center rounded-sm"><Shirt className="w-4 h-4 text-white" /></div>
            <span className="font-display text-lg tracking-tight font-semibold">MOCKUP STUDIO</span>
          </Link>

          <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(""); }}>
            <TabsList className="grid grid-cols-2 w-full rounded-sm mb-6">
              <TabsTrigger value="login" className="rounded-sm text-xs uppercase tracking-[0.15em]" data-testid="tab-login">Entrar</TabsTrigger>
              <TabsTrigger value="register" className="rounded-sm text-xs uppercase tracking-[0.15em]" data-testid="tab-register">Crear cuenta</TabsTrigger>
            </TabsList>

            <div className="mb-2">
              <h2 className="font-display text-3xl tracking-tight font-semibold">{tab === "login" ? "Bienvenido de nuevo" : "Crea tu cuenta"}</h2>
              <p className="text-neutral-500 text-sm mt-1">{tab === "login" ? "Entra con tu email y contraseña." : "Regístrate y recibe 5 créditos gratis."}</p>
            </div>

            <form onSubmit={submit} className="flex flex-col gap-4 mt-5">
              {tab === "register" && (
                <div className="flex flex-col gap-1.5">
                  <Label className="text-xs uppercase tracking-wider text-neutral-500">Nombre</Label>
                  <div className="relative">
                    <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre" className="rounded-sm pl-9" data-testid="name-input" />
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Email</Label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com" className="rounded-sm pl-9" data-testid="email-input" />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Contraseña</Label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 6 caracteres" className="rounded-sm pl-9" data-testid="password-input" />
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-sm p-2.5" data-testid="auth-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
                </div>
              )}

              <Button type="submit" disabled={busy} className="rounded-sm gap-2 bg-[#002FA7] hover:bg-[#00248a] h-11" data-testid="auth-submit-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {tab === "login" ? "Entrar" : "Crear cuenta"}
              </Button>
            </form>

            <div className="flex items-center gap-3 my-6">
              <div className="h-px bg-border flex-1" />
              <span className="text-xs uppercase tracking-[0.2em] text-neutral-400">o</span>
              <div className="h-px bg-border flex-1" />
            </div>

            <Button onClick={login} variant="outline" className="rounded-sm gap-2 w-full h-11" data-testid="google-login-btn">
              <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.23 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
              Continuar con Google
            </Button>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
